/**
 * Upload engine.
 *
 * Files < `MULTIPART_THRESHOLD` go through a single presigned PUT.
 * Larger files use S3 multipart: presign-mpu → upload parts in parallel
 * (concurrency `PART_CONCURRENCY`) → complete-mpu. Failures along the way
 * trigger abort-mpu so we don't leak orphaned uploads.
 *
 * Progress is reported via a callback driven by xhr.upload.onprogress.
 * Resume after refresh is not supported in this commit (IndexedDB tracking
 * lands in a follow-up).
 */

import { api } from '../lib/api';

const MULTIPART_THRESHOLD = 5 * 1024 * 1024; // 5 MiB
const PART_SIZE = 8 * 1024 * 1024; // 8 MiB
const PART_CONCURRENCY = 3;

export type UploadStatus = 'pending' | 'uploading' | 'done' | 'error' | 'cancelled';

export interface UploadItem {
  id: string;
  file: File;
  key: string;
  size: number;
  uploaded: number;
  status: UploadStatus;
  error?: string;
  abort: () => void;
}

export interface UploadCallbacks {
  onUpdate: (item: UploadItem) => void;
  onComplete: (item: UploadItem) => void;
}

interface Context {
  accountId: string;
  bucket: string;
  keyPrefix: string;
}

export function startUpload(file: File, ctx: Context, cb: UploadCallbacks): UploadItem {
  const aborter = new AbortController();
  const item: UploadItem = {
    id: cryptoRandomId(),
    file,
    key: ctx.keyPrefix + file.name,
    size: file.size,
    uploaded: 0,
    status: 'pending',
    abort: () => aborter.abort(),
  };

  if (file.size < MULTIPART_THRESHOLD) {
    void runSinglePut(item, ctx, cb, aborter.signal);
  } else {
    void runMultipart(item, ctx, cb, aborter.signal);
  }

  return item;
}

// ── Single PUT ──────────────────────────────────────────────────

async function runSinglePut(
  item: UploadItem,
  ctx: Context,
  cb: UploadCallbacks,
  signal: AbortSignal,
) {
  try {
    item.status = 'uploading';
    cb.onUpdate(item);

    const { url, headers } = await api.post<{ url: string; headers: Record<string, string> }>(
      `/api/accounts/${ctx.accountId}/buckets/${encodeURIComponent(ctx.bucket)}/objects/presign-put`,
      {
        key: item.key,
        contentType: item.file.type || 'application/octet-stream',
        sizeBytes: item.size,
        expiresIn: 900,
      },
    );

    await xhrPut(
      url,
      headers,
      item.file,
      (sent) => {
        item.uploaded = sent;
        cb.onUpdate(item);
      },
      signal,
    );

    item.uploaded = item.size;
    item.status = 'done';
    cb.onUpdate(item);
    cb.onComplete(item);
  } catch (e) {
    fail(item, cb, e);
  }
}

// ── Multipart ───────────────────────────────────────────────────

interface PartUrl {
  partNumber: number;
  url: string;
}

async function runMultipart(
  item: UploadItem,
  ctx: Context,
  cb: UploadCallbacks,
  signal: AbortSignal,
) {
  let sessionId: string | null = null;
  try {
    item.status = 'uploading';
    cb.onUpdate(item);

    const partCount = Math.ceil(item.size / PART_SIZE);
    const initRes = await api.post<{ sessionId: string; uploadId: string; partUrls: PartUrl[] }>(
      `/api/accounts/${ctx.accountId}/buckets/${encodeURIComponent(ctx.bucket)}/objects/presign-mpu`,
      {
        key: item.key,
        contentType: item.file.type || 'application/octet-stream',
        partSize: PART_SIZE,
        partCount,
        sizeBytes: item.size,
        expiresIn: 3600,
      },
    );
    sessionId = initRes.sessionId;

    const partProgress = new Array<number>(partCount).fill(0);
    const partETags: { partNumber: number; etag: string }[] = new Array(partCount);

    let cursor = 0;
    const errors: unknown[] = [];

    async function worker() {
      while (cursor < partCount) {
        const i = cursor++;
        const start = i * PART_SIZE;
        const end = Math.min(start + PART_SIZE, item.size);
        const chunk = item.file.slice(start, end);
        const partInfo = initRes.partUrls[i];
        if (!partInfo) throw new Error(`missing_part_url:${i + 1}`);
        try {
          const etag = await xhrPutPart(
            partInfo.url,
            chunk,
            (sent) => {
              partProgress[i] = sent;
              item.uploaded = partProgress.reduce((a, b) => a + b, 0);
              cb.onUpdate(item);
            },
            signal,
          );
          partETags[i] = { partNumber: partInfo.partNumber, etag };
        } catch (e) {
          errors.push(e);
          throw e;
        }
      }
    }

    await Promise.all(Array.from({ length: PART_CONCURRENCY }, worker));

    if (errors.length > 0) throw errors[0];

    await api.post(
      `/api/accounts/${ctx.accountId}/buckets/${encodeURIComponent(ctx.bucket)}/objects/mpu/${sessionId}/complete`,
      { parts: partETags },
    );

    item.uploaded = item.size;
    item.status = 'done';
    cb.onUpdate(item);
    cb.onComplete(item);
  } catch (e) {
    if (sessionId) {
      api
        .delete(
          `/api/accounts/${ctx.accountId}/buckets/${encodeURIComponent(ctx.bucket)}/objects/mpu/${sessionId}`,
        )
        .catch(() => {});
    }
    fail(item, cb, e);
  }
}

// ── HTTP helpers ────────────────────────────────────────────────

function xhrPut(
  url: string,
  headers: Record<string, string>,
  body: Blob,
  onProgress: (sent: number) => void,
  signal: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`upload_failed:${xhr.status}:${xhr.responseText.slice(0, 200)}`));
    };
    xhr.onerror = () => reject(new Error('network_error'));
    xhr.onabort = () => reject(new Error('aborted'));
    signal.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.send(body);
  });
}

function xhrPutPart(
  url: string,
  body: Blob,
  onProgress: (sent: number) => void,
  signal: AbortSignal,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const etag = (xhr.getResponseHeader('ETag') ?? '').replace(/^"|"$/g, '');
        if (!etag) reject(new Error('missing_etag'));
        else resolve(etag);
      } else {
        reject(new Error(`part_failed:${xhr.status}`));
      }
    };
    xhr.onerror = () => reject(new Error('network_error'));
    xhr.onabort = () => reject(new Error('aborted'));
    signal.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.send(body);
  });
}

function fail(item: UploadItem, cb: UploadCallbacks, e: unknown) {
  if (e instanceof Error && e.message === 'aborted') {
    item.status = 'cancelled';
  } else {
    item.status = 'error';
    item.error = e instanceof Error ? e.message : String(e);
  }
  cb.onUpdate(item);
  cb.onComplete(item);
}

function cryptoRandomId(): string {
  const bytes = new Uint8Array(8);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}
